import {
  Onboarding,
  OnboardingPage,
  OnboardingQuestion,
} from '../generated/gql-types';
import { getApolloClient } from '../lib/getApolloClient';
import { GET_ONBOARDING, GET_ONBOARDING_FOR_USER } from './onboarding.gql';
import { User } from '../generated/gql-types';
import { fetchCurrentUser } from '../user/user.action';

type CustomAttributeValueOwner = 'USER' | 'CLIENT';

function normaliseCustomAttributeOwner(
  objectToManipulate: string | null | undefined,
): CustomAttributeValueOwner {
  if ((objectToManipulate || '').toUpperCase() === 'CLIENT') {
    return 'CLIENT';
  }
  return 'USER';
}

function hasDeclaredObjectToManipulate(
  objectToManipulate: string | null | undefined,
): boolean {
  return (
    objectToManipulate != null && String(objectToManipulate).trim() !== ''
  );
}

/**
 * Merges user customAttributes into onboarding questions that map to the user.
 * Skips questions scoped to CLIENT so `getOnboarding` values (from the client
 * record) are not overwritten by the user's customAttributes.
 */
const populateQuestionValueFromCustomAttributes = (
  question: OnboardingQuestion,
  user: User | null,
  inheritedOwner?: CustomAttributeValueOwner,
): void => {
  if (
    question.objectAttributeToManipulate === 'customAttributes' &&
    user?.customAttributes
  ) {
    const declared = hasDeclaredObjectToManipulate(question.objectToManipulate)
      ? normaliseCustomAttributeOwner(question.objectToManipulate)
      : undefined;
    const effectiveOwner: CustomAttributeValueOwner =
      declared ?? inheritedOwner ?? 'USER';

    if (effectiveOwner !== 'CLIENT') {
      const customAttribute = user.customAttributes.find(
        (attr) => attr?.ref === question.key,
      );
      if (customAttribute?.value) {
        question.value = customAttribute.value;
      }
    }
  }

  const ownerForChildren: CustomAttributeValueOwner | undefined =
    question.objectAttributeToManipulate === 'customAttributes'
      ? hasDeclaredObjectToManipulate(question.objectToManipulate)
        ? normaliseCustomAttributeOwner(question.objectToManipulate)
        : inheritedOwner ?? 'USER'
      : inheritedOwner;

  if (question.childQuestions) {
    question.childQuestions.forEach((childQuestion) => {
      populateQuestionValueFromCustomAttributes(
        childQuestion,
        user,
        ownerForChildren,
      );
    });
  }
};

/**
 * Populates onboarding questions that store data on the user with values from
 * `user.customAttributes`. Client-scoped custom attribute questions keep the
 * values returned by the API.
 */
const populateOnboardingQuestionsFromCustomAttributes = (
  onboarding: any,
  user: User | null,
): void => {
  if (!onboarding?.onboardingJSON?.pages || !user) {
    return;
  }

  onboarding.onboardingJSON.pages.forEach((page: OnboardingPage) => {
    if (page.questions) {
      page.questions.forEach((question: OnboardingQuestion) => {
        populateQuestionValueFromCustomAttributes(question, user);
      });
    }
  });
};

export const onboardingActions = {
  fetchOnboarding: async () => {
    try {
      const { data } = await getApolloClient().query({ query: GET_ONBOARDING });

      data?.getOnboarding.onboardingJSON.pages.sort(
        (a: OnboardingPage, b: OnboardingPage) => Number(a.id) - Number(b.id),
      );

      // Populate customAttributes values
      const currentUser = await fetchCurrentUser();
      populateOnboardingQuestionsFromCustomAttributes(
        data?.getOnboarding,
        currentUser,
      );

      return data?.getOnboarding;
    } catch (error) {
      console.error('Error fetching onboarding:', error);
      return null;
    }
  },

  fetchAndSetOnboarding: async () => {
    try {
      const { data } = await getApolloClient().query({ query: GET_ONBOARDING });

      data?.getOnboarding.onboardingJSON.pages.sort(
        (a: OnboardingPage, b: OnboardingPage) => Number(a.id) - Number(b.id),
      );

      // Populate customAttributes values
      const currentUser = await fetchCurrentUser();
      populateOnboardingQuestionsFromCustomAttributes(
        data?.getOnboarding,
        currentUser,
      );

      return data?.getOnboarding;
    } catch (error) {
      console.error('Error fetching onboarding:', error);
      return null;
    }
  },

  fetchOnboardingForUser: async (userId: string): Promise<Onboarding> => {
    try {
      const { data } = await getApolloClient().query({
        query: GET_ONBOARDING_FOR_USER,
        variables: { userId },
      });
      return data?.getOnboardingForUser;
    } catch (error) {
      console.error('Error fetching onboarding for user:', error);
      throw error;
    }
  },
};
